import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { formatEther, parseEther } from 'viem';

import { PunksmarketV1Service, emptyPunksFilter } from '@/services/punksmarket-v1.service';
import { Web3Service } from '@/services/web3.service';
import { environment } from 'src/environments/environment';
import { OnChainBidLabelComponent } from '@/components/on-chain-bid-label/on-chain-bid-label.component';
import {
  onChainBidEscrowHint,
  placeOnChainBidButtonLabel,
} from '@/utils/native-market-bid-labels';
import { PunksmarketIndexerBid } from '@/models/unified-market-bid';

/** `targeted` = single-punk allowlist (item page). `criteria` = full filter UI (future entry points). */
export type PunksmarketPlaceBidMode = 'targeted' | 'criteria';
export type PunksmarketPlaceBidStep = 'form' | 'success';

@Component({
  selector: 'app-punksmarket-place-bid-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, OnChainBidLabelComponent],
  templateUrl: './punksmarket-place-bid-modal.component.html',
  styleUrls: ['./punksmarket-place-bid-modal.component.scss'],
})
export class PunksmarketPlaceBidModalComponent implements OnChanges {
  readonly placeOnChainBidButtonLabel = placeOnChainBidButtonLabel;
  readonly onChainBidEscrowHint = onChainBidEscrowHint;

  @Input() isActive = false;
  @Input() punkId: string | null = null;
  @Input() mode: PunksmarketPlaceBidMode = 'targeted';
  @Input() cloneFromBid: PunksmarketIndexerBid | null = null;
  @Output() modalClosed = new EventEmitter<void>();
  @Output() bidPlaced = new EventEmitter<void>();

  bidAmountEth = '';
  settlementEth = '0';
  showSettlementReward = false;
  submitting = false;
  errorMessage: string | null = null;
  step: PunksmarketPlaceBidStep = 'form';
  placedBidAmountEth: string | null = null;
  placedSettlementEth: string | null = null;
  placedTxHash: string | null = null;

  readonly environment = environment;
  parseFloat = parseFloat;

  constructor(
    private readonly punksmarketV1Svc: PunksmarketV1Service,
    private readonly web3Svc: Web3Service,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive']?.currentValue) {
      this.resetForm();
      this.applyCloneFromBid();
    }
    if (changes['cloneFromBid']?.currentValue && this.isActive) {
      this.applyCloneFromBid();
    }
  }

  get isTargetedMode(): boolean {
    return this.mode === 'targeted';
  }

  get placedTxShortHash(): string | null {
    const hash = this.placedTxHash;
    if (!hash || hash.length < 12) {
      return hash;
    }
    return `${hash.slice(0, 6)}...${hash.slice(-6)}`;
  }

  closeModal(): void {
    this.errorMessage = null;
    this.modalClosed.emit();
  }

  toggleSettlementReward(): void {
    this.showSettlementReward = !this.showSettlementReward;
    if (!this.showSettlementReward) {
      this.settlementEth = '0';
    }
  }

  private resetForm(): void {
    this.bidAmountEth = '';
    this.settlementEth = '0';
    this.showSettlementReward = false;
    this.errorMessage = null;
    this.step = 'form';
    this.placedBidAmountEth = null;
    this.placedSettlementEth = null;
    this.placedTxHash = null;
  }

  finishSuccess(): void {
    this.closeModal();
  }

  private applyCloneFromBid(): void {
    const bid = this.cloneFromBid;
    if (!bid) {
      return;
    }
    try {
      this.bidAmountEth = formatEther(BigInt(bid.bidWei));
      const settlement = BigInt(bid.settlementWei ?? '0');
      if (settlement > 0n) {
        this.showSettlementReward = true;
        this.settlementEth = formatEther(settlement);
      }
    } catch {
      /* keep defaults */
    }
  }

  async submit(): Promise<void> {
    this.errorMessage = null;

    if (!this.punkId && this.isTargetedMode) {
      this.errorMessage = 'Missing punk id.';
      return;
    }

    const bidAmountStr = String(this.bidAmountEth).trim();
    const amount = parseFloat(bidAmountStr);
    if (!bidAmountStr || Number.isNaN(amount) || amount <= 0) {
      this.errorMessage = 'Enter a bid amount greater than zero.';
      return;
    }

    this.submitting = true;
    try {
      const settlementStr = String(this.settlementEth ?? '0').trim() || '0';
      const bidWei = parseEther(bidAmountStr);
      const settlementWei = parseEther(settlementStr);
      const includeIds =
        this.isTargetedMode && this.punkId ? [Number(this.punkId)] : [];
      const hash = await this.punksmarketV1Svc.placeBid({
        bidWei,
        settlementWei,
        criteria: emptyPunksFilter(),
        includeIds,
        excludeIds: [],
      });
      if (hash) {
        await this.web3Svc.waitForTransaction(hash);
        this.placedBidAmountEth = bidAmountStr;
        this.placedSettlementEth = settlementStr;
        this.placedTxHash = hash;
        this.step = 'success';
        this.bidPlaced.emit();
      }
    } catch (err: unknown) {
      this.errorMessage =
        err instanceof Error ? err.message : 'Failed to place bid';
    } finally {
      this.submitting = false;
    }
  }
}
